import { inject, Injectable, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { merge, ReplaySubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NotificationLevel, NotificationsService } from '@eiq/notifications';
import { GetNextId } from '@eiq/shared/utils';

import { ToPlainText } from '@empathy/global/functions';
import { BlockModel, CustomTerm, OperatorByQuestionType, QuestionOption, QuestionPrefix, QuestionType, RatingType } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { DemographicQuestionsService } from './demographic-questions.service';
import { SurveyConditionalsService } from './survey-conditionals.service';
import { SurveyPositioningService } from './survey-positioning.service';
import { IBlockForm } from '../models/blocks/block-form.interface';
import { LoopSettingsSelection } from '../models/blocks/loop-settings-selection.interface';
import { RemoveConditionalsRequest } from '../models/conditions/remove-conditionals-request.model';
import { MainTabs } from '../models/main-tabs.enum';
import { DuplicatedCodeSuffix } from '../models/question-code.constants';
import { QuestionTypeChangeOptions } from '../models/question-type-change.constants';
import { QuestionTypeConfig } from '../models/question-type-config.constants';
import { SideBlocksTabs } from '../models/side-blocks-tabs.enum';
import { SideHiddenTabs } from '../models/side-hidden-tabs.enum';
import { SideOptionTabs } from '../models/side-option-tabs.enum';
import { SideTabs } from '../models/side-tabs.enum';
import { ISourceForm } from '../models/sources/source-form.model';
import { PageForm } from '../models/survey-editor/page-form.model';
import { SurveyEditor } from '../models/survey-editor/survey-editor.model';
import { QuestionFormInstanceByType } from '../models/type-forms/form-type-instance.constant';
import { QuestionForm } from '../models/type-forms/question-form.model';
import { QuestionOptionForm } from '../models/type-forms/question-option-form.model';

@Injectable({ providedIn: 'root' })
export class SurveyEditorService {
  readonly loopSettingsSelection$ = signal<LoopSettingsSelection | null>(null);

  activeTab$ = signal<SideTabs | SideOptionTabs | SideBlocksTabs | SideHiddenTabs>(SideTabs.Preview);
  mainTab$ = signal<MainTabs>(MainTabs.SurveyEditor);

  questionAdded$ = new ReplaySubject<void>();
  questionHiddenAdded$ = new ReplaySubject<void>();
  questions$ = new ReplaySubject<Question[]>(1);
  questionsHidden$ = new ReplaySubject<FormArray>();
  sources$ = new ReplaySubject<FormGroup<ISourceForm>[]>();

  endPageScreenActive$ = signal<boolean>(false);
  surveyEditor$ = signal<SurveyEditor>(null);
  welcomeScreenActive$ = signal<boolean>(false);

  renderedPages: string[] = [];

  private conditionalService = inject(SurveyConditionalsService);
  private demographicQuestionsService = inject(DemographicQuestionsService);
  private positioningService = inject(SurveyPositioningService);
  private notificationService = inject(NotificationsService);

  private refreshListener: Subscription;

  openLoopSettings(block: FormGroup<IBlockForm>, trigger: HTMLElement): void {
    this.loopSettingsSelection$.set({ block, trigger });
    this.activeTab$.set(SideBlocksTabs.Settings);
  }

  closeLoopSettings(): void {
    this.loopSettingsSelection$.set(null);
    if (this.activeTab$() === SideBlocksTabs.Settings) {
      this.activeTab$.set(SideBlocksTabs.Conditions);
    }
  }

  initForm(survey: SurveyEditor): void {
    this.surveyEditor$.set(survey);
    this.conditionalService.init(survey);
    this.positioningService.init(survey, this.conditionalService, this);

    this.selectWelcomeScreen();

    if (this.surveyEditor$().blocks.count > 0) {
      this.surveyEditor$().blocks.activeIndex$.set(0);
    }

    if (this.surveyEditor$().sources.count$() > 0) {
      this.surveyEditor$().sources.activeIndex$.set(0);
    }
    this.sources$.next(this.surveyEditor$().sources.all);

    this.refreshQuestionsList();
    this.questionsHidden$.next(this.surveyEditor$().questionsHiddenCtrls);
  }

  addPageBreak(amendPages: boolean, activeIndex: number = null): string {
    const pageId = `page_${this.surveyEditor$().pages.length + 1}`;
    const page = new PageForm(pageId);

    this.surveyEditor$().pushPage(page);

    if (!activeIndex) {
      activeIndex = this.surveyEditor$().activeQuestionIndex === -1 ? 0 : this.surveyEditor$().activeQuestionIndex;
    }

    const nextQuestion = this.surveyEditor$().questions[activeIndex + 1];
    if (!nextQuestion) {
      return pageId;
    }

    const currentPageId = nextQuestion.pageId;

    let index = activeIndex + 1;
    while (index < this.surveyEditor$().questions.length) {
      if (currentPageId !== this.surveyEditor$().questions[index].pageId) {
        break;
      }
      this.surveyEditor$().questionsCtrls.at(index).get('pageId').setValue('page_temp');
      index++;
    }

    if (amendPages) {
      this.surveyEditor$().amendPages();
      this.refreshQuestionsList();
    }

    return pageId;
  }

  addQuestion(questionType: QuestionType): string {
    const questionToAdd = QuestionFormInstanceByType(questionType);
    const { activeIndex, nextId, pageId } = this.mapActiveAddQuestionId();
    questionToAdd.initEmpty(nextId, pageId);

    const activeQuestion = this.surveyEditor$().activeQuestion?.value;
    const activeQuestionBlock = activeQuestion ? this.surveyEditor$().blocks.byGroupedQId(activeQuestion.id) : undefined;

    if (this.surveyEditor$().form.get('settings').get('monitorTerms').get('monitorProblematicInd').value && questionToAdd.form.get('monitorTerms')) {
      questionToAdd.form.get('monitorTerms').get('monitorProblematicInd').setValue(true);
    }

    if (activeQuestionBlock) {
      this.surveyEditor$().blocks.assertQuestionBlock(activeQuestionBlock.id);
    }
    this.pushQuestion(activeIndex, questionToAdd);

    if (activeQuestionBlock) {
      this.surveyEditor$().blocks.insertQuestionToBlock(activeQuestionBlock.id, questionToAdd.form.value.id, activeQuestion.id);
    }

    this.surveyEditor$().amendPages();
    this.refreshQuestionsList();
    return questionToAdd.form.value.id;
  }

  addHiddenQuestion(): void {
    const questionHiddenToAdd = QuestionFormInstanceByType(QuestionType.HiddenChoice);
    const { activeIndex, nextId } = this.mapAddHiddenQuestionIds();

    questionHiddenToAdd.initHiddenEmpty(nextId);
    this.pushHiddenQuestions(activeIndex, questionHiddenToAdd);
  }

  addDemographics(): void {
    let newQuestionIds = this.mapAddQuestionIds();
    let questionFormToAdd = this.demographicQuestionsService.mapGenderQuestion(newQuestionIds.nextId, newQuestionIds.pageId);
    this.pushQuestion(newQuestionIds.activeIndex, questionFormToAdd, false);

    newQuestionIds = this.mapAddQuestionIds();
    questionFormToAdd = this.demographicQuestionsService.mapAgeQuestion(newQuestionIds.nextId, newQuestionIds.pageId);
    this.pushQuestion(newQuestionIds.activeIndex, questionFormToAdd, false);

    let newHiddenQuestionIds = this.mapAddHiddenQuestionIds();
    let questionHiddenFormToAdd = this.demographicQuestionsService.mapAgeHiddenQuestion(
      newHiddenQuestionIds.nextId,
      `${QuestionPrefix.Question}${newQuestionIds.nextId}`
    );
    this.pushHiddenQuestions(newHiddenQuestionIds.activeIndex, questionHiddenFormToAdd, false);

    newQuestionIds = this.mapAddQuestionIds();
    questionFormToAdd = this.demographicQuestionsService.mapAgeTerminateQuestion(newQuestionIds.nextId, newQuestionIds.pageId);
    this.pushQuestion(newQuestionIds.activeIndex, questionFormToAdd, false);

    newQuestionIds = this.mapAddQuestionIds();
    questionFormToAdd = this.demographicQuestionsService.mapRegionQuestion(newQuestionIds.nextId, newQuestionIds.pageId);
    this.pushQuestion(newQuestionIds.activeIndex, questionFormToAdd, false);

    newHiddenQuestionIds = this.mapAddHiddenQuestionIds();
    questionHiddenFormToAdd = this.demographicQuestionsService.mapRegionHiddenQuestion(
      newHiddenQuestionIds.nextId,
      `${QuestionPrefix.Question}${newQuestionIds.nextId}`
    );
    this.pushHiddenQuestions(newHiddenQuestionIds.activeIndex, questionHiddenFormToAdd, false);

    newQuestionIds = this.mapAddQuestionIds();
    questionFormToAdd = this.demographicQuestionsService.mapEmploymentStatusQuestion(newQuestionIds.nextId, newQuestionIds.pageId);
    this.pushQuestion(newQuestionIds.activeIndex, questionFormToAdd, false);

    newQuestionIds = this.mapAddQuestionIds();
    questionFormToAdd = this.demographicQuestionsService.mapOccupationalGroupQuestion(newQuestionIds.nextId, newQuestionIds.pageId);
    this.pushQuestion(newQuestionIds.activeIndex, questionFormToAdd, false);

    newHiddenQuestionIds = this.mapAddHiddenQuestionIds();
    questionHiddenFormToAdd = this.demographicQuestionsService.mapSocialGroupHiddenQuestion(
      newHiddenQuestionIds.nextId,
      `${QuestionPrefix.Question}${newQuestionIds.nextId}`
    );
    this.pushHiddenQuestions(newHiddenQuestionIds.activeIndex, questionHiddenFormToAdd, false);

    newHiddenQuestionIds = this.mapAddHiddenQuestionIds();
    questionHiddenFormToAdd = this.demographicQuestionsService.mapSocialClassHiddenQuestion(
      newHiddenQuestionIds.nextId,
      `${QuestionPrefix.Question}${newQuestionIds.nextId}`
    );
    this.pushHiddenQuestions(newHiddenQuestionIds.activeIndex, questionHiddenFormToAdd, false);

    newQuestionIds = this.mapAddQuestionIds();
    const childrenQuestionId = newQuestionIds.nextId;
    questionFormToAdd = this.demographicQuestionsService.mapChildrenQuestion(newQuestionIds.nextId, newQuestionIds.pageId);
    this.pushQuestion(newQuestionIds.activeIndex, questionFormToAdd, false);

    newQuestionIds = this.mapAddQuestionIds();
    questionFormToAdd = this.demographicQuestionsService.mapChildrenAgeQuestion(newQuestionIds.nextId, newQuestionIds.pageId, childrenQuestionId);
    this.pushQuestion(newQuestionIds.activeIndex, questionFormToAdd);
  }

  copyActiveQuestion(): void {
    const questionId = this.surveyEditor$().activeQuestion.value.id;
    const questionToCopy = this.surveyEditor$().questions.find((q) => q.id === questionId);

    if (!questionToCopy) {
      return;
    }

    this.copyQuestion(questionToCopy);
  }

  copyQuestions(questionsToCopy: Question[]): void {
    questionsToCopy.forEach((questionToCopy) => {
      this.copyQuestion(questionToCopy);
    });
  }

  duplicateQuestions(questionsToDuplicate: Question[]): void {
    questionsToDuplicate.forEach((questionToDuplicate) => {
      const questionToAdd = QuestionFormInstanceByType(questionToDuplicate.type);
      const { activeIndex, nextId, pageId } = this.mapActiveAddQuestionId();
      const newId = `${QuestionPrefix.Question}${nextId}`;

      const activeQuestion = this.surveyEditor$().activeQuestion?.value;
      const activeQuestionBlock = activeQuestion ? this.surveyEditor$().blocks.byGroupedQId(activeQuestion.id) : undefined;

      if (activeQuestionBlock) {
        this.surveyEditor$().blocks.assertQuestionBlock(activeQuestionBlock.id);
      }

      questionToDuplicate.code = this.mapDuplicatedQuestionCode(questionToDuplicate.code);
      questionToDuplicate.id = newId;
      questionToDuplicate.pageId = pageId;
      questionToDuplicate.visibleWhen = [];
      questionToDuplicate.options?.forEach((option) => (option.visibleWhen = []));
      questionToDuplicate.cards?.forEach((card) => (card.visibleWhen = []));

      questionToAdd.init(questionToDuplicate);
      this.pushQuestion(activeIndex, questionToAdd);

      if (activeQuestionBlock) {
        this.surveyEditor$().blocks.insertQuestionToBlock(activeQuestionBlock.id, newId, activeQuestion.id);
      }

      this.surveyEditor$().amendPages();
    });

    this.refreshQuestionsList();

    this.notificationService.open({
      level: NotificationLevel.Success,
      title: `${questionsToDuplicate.length} ${questionsToDuplicate.length === 1 ? 'question' : 'questions'} duplicated`,
      message: ''
    });
  }

  changeQuestionType(targetType: QuestionType): void {
    const question = this.surveyEditor$().activeQuestion?.value as Question;

    if (!question || !QuestionTypeChangeOptions[question.type]?.includes(targetType)) {
      return;
    }

    const activeIndex = this.surveyEditor$().activeQuestionIndex;

    const questionToChange = QuestionFormInstanceByType(targetType);
    questionToChange.init(question);

    if (targetType === QuestionType.CarouselRating && question.options?.length > 0) {
      questionToChange.form.get('minValueText').setValue(ToPlainText(question.options[0].text));
      questionToChange.form.get('maxValueText').setValue(ToPlainText(question.options[question.options.length - 1].text));
    }

    if (question.type === QuestionType.CarouselRating && question.ratingType !== RatingType.descriptive) {
      this.setCarouselOptionsFromRatingScale(questionToChange.form, question);
    }

    this.surveyEditor$().questionsCtrls.setControl(activeIndex, questionToChange.form);
    this.surveyEditor$().addItemsToQuestionIdsMap([questionToChange.form.value as Question]);
    this.refreshQuestionsList();
    this.refreshQuestionsArrayWhenTitleChanges();

    const allowedOperators = targetType === QuestionType.CarouselRating ? [] : OperatorByQuestionType[targetType]['default'];
    this.conditionalService.removeIncompatibleConditionsById(question.id, allowedOperators);

    this.notificationService.open({
      level: NotificationLevel.Success,
      title: `${question.code} changed to ${QuestionTypeConfig[targetType].text.toLowerCase()}`,
      message: ''
    });
  }

  conditionsAffectedByTypeChange(targetType: QuestionType): { affectedQuestions: Question[]; affectedBlocks: BlockModel[] } {
    const question = this.surveyEditor$().activeQuestion?.value as Question;

    if (!question || !QuestionTypeChangeOptions[question.type]?.includes(targetType)) {
      return { affectedQuestions: [], affectedBlocks: [] };
    }

    const allowedOperators = targetType === QuestionType.CarouselRating ? [] : OperatorByQuestionType[targetType]['default'];

    return {
      affectedQuestions: [
        ...this.surveyEditor$().affectedQuestionsByIncompatibleCondition(question.id, allowedOperators),
        ...this.surveyEditor$().affectedHiddenQuestionsByIncompatibleCondition(question.id, allowedOperators)
      ],
      affectedBlocks: this.surveyEditor$().blocks.affectedBlocksByIncompatibleCondition(question.id, allowedOperators)
    };
  }

  selectWelcomeScreen(): void {
    this.surveyEditor$().activateQuestion = null;
    this.surveyEditor$().deactivateActiveQuestion();
    this.surveyEditor$().deactivateActiveHiddenQuestion();
    this.welcomeScreenActive$.set(true);
    this.activeTab$.set(SideTabs.Preview);
    this.refreshQuestionsList();
    this.endPageScreenActive$.set(false);
    this.surveyEditor$().sources.activeVariable$.set(null);
  }

  selectEndPageScreen(): void {
    this.surveyEditor$().activateQuestion = null;
    this.surveyEditor$().deactivateActiveQuestion();
    this.surveyEditor$().deactivateActiveHiddenQuestion();
    this.endPageScreenActive$.set(true);
    this.activeTab$.set(SideTabs.Preview);
    this.refreshQuestionsList();
    this.welcomeScreenActive$.set(false);
    this.surveyEditor$().sources.activeVariable$.set(null);
  }

  selectQuestion(newActiveQuestionId: string): void {
    this.surveyEditor$().activateQuestion = newActiveQuestionId;
    this.surveyEditor$().activateHiddenQuestion = null;
    this.surveyEditor$().deactivateActiveHiddenQuestion();
    this.refreshQuestionsArrayWhenTitleChanges();
    this.activeTab$.set(SideTabs.Preview);
    this.welcomeScreenActive$.set(false);
    this.endPageScreenActive$.set(false);
    this.surveyEditor$().sources.activeVariable$.set(null);
  }

  selectVariable(newActiveVariableId: string): void {
    this.surveyEditor$().activateQuestion = null;
    this.surveyEditor$().deactivateActiveQuestion();
    this.surveyEditor$().deactivateActiveHiddenQuestion();
    this.refreshQuestionsList();
    this.welcomeScreenActive$.set(false);
    this.endPageScreenActive$.set(false);
    this.surveyEditor$().sources.editVariable(newActiveVariableId);
    this.activeTab$.set(SideTabs.Comments);
  }

  selectHiddenQuestion(newActiveHiddenQuestionId: string): void {
    this.surveyEditor$().activateQuestion = null;
    if (!newActiveHiddenQuestionId && this.surveyEditor$().questionsHidden?.length > 0) {
      this.surveyEditor$().activateHiddenQuestion = this.surveyEditor$().questionsHidden[0].id;
    } else {
      this.surveyEditor$().activateHiddenQuestion = newActiveHiddenQuestionId;
    }
    this.surveyEditor$().deactivateActiveQuestion();
    this.refreshHiddenQuestionsArrayWhenTitleChanges();
    this.activeTab$.set(SideOptionTabs.Settings);
    this.questionsHidden$.next(this.surveyEditor$().questionsHiddenCtrls);
    this.welcomeScreenActive$.set(false);
    this.endPageScreenActive$.set(false);
    this.surveyEditor$().sources.activeVariable$.set(null);
  }

  removeQuestionById(questionId: string): void {
    const indexToRemove = this.surveyEditor$().getQuestionIndex(questionId);

    this.conditionalService.removeAffectedConditionsById(questionId);
    this.conditionalService.removeAffectedHiddenConditionsById(questionId);
    this.surveyEditor$()
      .blocks.affectedConditions(questionId)
      .forEach((blockId: string) => {
        const visibleWhen = this.surveyEditor$().blocks.all.controls.find((block) => block.get('id').value === blockId).controls.visibleWhen;
        this.conditionalService.removeConditionsByQuestionId(visibleWhen, questionId);
      });

    this.surveyEditor$().blocks.removeBlocksAffectedBy(questionId);

    this.surveyEditor$().removeQuestion(indexToRemove);

    if (indexToRemove > 0) {
      this.selectQuestion(this.findPreviousQuestionId(indexToRemove));
    }

    this.surveyEditor$().amendPages();
    this.refreshQuestionsList();
  }

  removeActiveQuestion(): void {
    const indexToRemove = this.surveyEditor$().activeQuestionIndex;

    this.conditionalService.removeAffectedConditions();
    this.conditionalService.removeAffectedHiddenConditions();
    this.conditionalService.removeAffectedBlockConditions();

    this.surveyEditor$().blocks.removeBlocksAffectedBy(this.surveyEditor$().activeQuestion.get('id').value);

    this.surveyEditor$().removeQuestion(indexToRemove);

    if (indexToRemove > 0) {
      this.selectQuestion(this.findPreviousQuestionId(indexToRemove));
    }

    this.surveyEditor$().amendPages();
    this.refreshQuestionsList();
  }

  removeActiveHiddenQuestion(): void {
    const indexToRemove = this.surveyEditor$().activeHiddenQuestionIndex;

    this.surveyEditor$().removeHiddenQuestion(indexToRemove);

    if (indexToRemove > 1) {
      this.selectHiddenQuestion(this.findPreviousHiddenQuestionId(indexToRemove - 1));
    } else {
      const initialQuestion = this.surveyEditor$().questionsHidden.find((q) => q.type === QuestionType.HiddenChoice);
      this.selectHiddenQuestion(initialQuestion ? initialQuestion.id : undefined);
    }

    this.refreshQuestionsList();
  }

  removePage(pageId: string): void {
    this.surveyEditor$().removePage(pageId);

    this.refreshQuestionsList();
  }

  removeOptionAt(optionIndex: number, optionId: string): void {
    this.surveyEditor$().activeQuestionOptions.removeAt(optionIndex);

    const affectedQuestionConditions = this.conditionalService.affectedQuestionConditionsByOptionRemoved(optionId);
    const affectedHiddenQuestionConditions = this.conditionalService.affectedHiddenQuestionConditionsByOptionRemoved(optionId);

    if (affectedQuestionConditions.length === 0 && affectedHiddenQuestionConditions.length === 0) {
      return;
    }

    this.conditionalService.removeHiddenConditions(
      new RemoveConditionalsRequest(this.surveyEditor$().activeQuestion.get('id').value, affectedHiddenQuestionConditions, optionId)
    );

    affectedQuestionConditions.forEach((qId) =>
      this.conditionalService.removeConditions(new RemoveConditionalsRequest(qId, [this.surveyEditor$().activeQuestion.get('id').value], optionId))
    );
  }

  removeCardAt(cardIndex: number, cardId: string): void {
    this.surveyEditor$().activeQuestionCards.removeAt(cardIndex);

    const affectedQuestionConditions = this.conditionalService.affectedQuestionConditionsByCardRemoved(cardId);
    const affectedHiddenQuestionConditions = this.conditionalService.affectedHiddenQuestionConditionsByCardRemoved(cardId);

    if (affectedQuestionConditions.length === 0 && affectedHiddenQuestionConditions.length === 0) {
      return;
    }

    this.conditionalService.removeHiddenConditions(
      new RemoveConditionalsRequest(this.surveyEditor$().activeQuestion.get('id').value, affectedHiddenQuestionConditions, undefined, cardId)
    );
    affectedQuestionConditions.forEach((qId) =>
      this.conditionalService.removeConditions(new RemoveConditionalsRequest(qId, [this.surveyEditor$().activeQuestion.get('id').value], undefined, cardId))
    );
  }

  questionCsvBlocksImport(csvContent: CustomTerm[]): number {
    return this.questionTermsCsvImport('blockedTerms', csvContent);
  }

  questionCsvFlagsImport(csvContent: CustomTerm[]): number {
    return this.questionTermsCsvImport('flaggedTerms', csvContent);
  }

  refreshQuestionsList(): void {
    this.questions$.next(this.surveyEditor$().questions.slice());
  }

  private questionTermsCsvImport(field: string, csvContent: CustomTerm[]): number {
    let addedTerms = 0;

    if (csvContent?.length) {
      const flaggedTermsArray = this.surveyEditor$().activeQuestion?.get('monitorTerms').get(field) as FormArray;

      csvContent.forEach((term) => {
        const isDuplicate = flaggedTermsArray.controls.some((control) => control.get('type')?.value === term.type && control.get('term')?.value === term.term);

        if (!isDuplicate) {
          flaggedTermsArray.push(
            new FormGroup({
              type: new FormControl(term.type),
              term: new FormControl(term.term)
            })
          );
          addedTerms++;
        }
      });

      const firstElementExists =
        (this.surveyEditor$().activeQuestion?.get('monitorTerms').get(field) as FormArray).controls.length &&
        (this.surveyEditor$().activeQuestion?.get('monitorTerms').get(field) as FormArray).at(0);

      const firstElementIsBlank = (this.surveyEditor$().activeQuestion?.get('monitorTerms').get(field) as FormArray).at(0).value.term === '';

      if (firstElementExists && firstElementIsBlank) {
        (this.surveyEditor$().activeQuestion?.get('monitorTerms').get(field) as FormArray).removeAt(0);
      }

      return addedTerms;
    }

    if (csvContent && !addedTerms) {
      return 0;
    }
  }

  private mapActiveAddQuestionId(): { activeIndex: number; nextId: number; pageId: string } {
    const nextId = this.surveyEditor$().lastQuestionId + 1;

    let pageId;
    let activeIndex;
    if (this.surveyEditor$().activeQuestion) {
      activeIndex = this.surveyEditor$().activeQuestionIndex;
      pageId = this.addPageBreak(false, this.surveyEditor$().activeQuestionIndex - 1);
    } else {
      activeIndex = this.surveyEditor$().questions.length;
      pageId = this.addPageBreak(false, this.surveyEditor$().questions.length - 1);
    }

    return { activeIndex, nextId, pageId };
  }

  private mapAddQuestionIds(): { activeIndex: number; nextId: number; pageId: string } {
    const nextId = this.surveyEditor$().lastQuestionId + 1;
    const activeIndex = this.surveyEditor$().questions.length;
    const pageId = this.addPageBreak(true, activeIndex - 1);

    return { activeIndex, nextId, pageId };
  }

  private mapAddHiddenQuestionIds(): { activeIndex: number; nextId: number } {
    const nextId = this.surveyEditor$().lastHiddenQuestionId + 1;
    const activeIndex = this.surveyEditor$().questionsHidden.length;

    return { activeIndex, nextId };
  }

  private pushQuestion(activeIndex: number, newQuestion: QuestionForm, emitEvent: boolean = true): void {
    this.surveyEditor$().pushQuestion(activeIndex, newQuestion);
    if (emitEvent) {
      this.selectQuestion(newQuestion.form.get('id').value);
      this.refreshQuestionsList();
      this.questionAdded$.next();
    }
  }

  private pushHiddenQuestions(activeIndex: number, newHiddenQuestion: QuestionForm, emitEvent: boolean = true): void {
    this.surveyEditor$().pushHiddenQuestion(activeIndex, newHiddenQuestion);

    if (emitEvent) {
      this.selectHiddenQuestion(newHiddenQuestion.form.get('id').value);
      this.questionsHidden$.next(this.surveyEditor$().questionsHiddenCtrls);
      this.questionHiddenAdded$.next();
    }
  }

  private refreshQuestionsArrayWhenTitleChanges(): void {
    this.refreshListener?.unsubscribe();
    this.refreshListener = merge(this.surveyEditor$().activeQuestion?.get('title').valueChanges, this.surveyEditor$().activeQuestion?.get('code').valueChanges)
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.refreshQuestionsList();
      });
  }

  private refreshHiddenQuestionsArrayWhenTitleChanges(): void {
    this.refreshListener?.unsubscribe();
    const streamsToMerge = [
      this.surveyEditor$().activeHiddenQuestion?.get('title').valueChanges,
      this.surveyEditor$().activeHiddenQuestion?.get('code').valueChanges
    ].filter((stream) => stream);

    if (streamsToMerge.length > 0) {
      this.refreshListener = merge(...streamsToMerge)
        .pipe(debounceTime(1000))
        .subscribe(() => {
          this.questionsHidden$.next(this.surveyEditor$().questionsHiddenCtrls);
        });
    }
  }

  private findPreviousQuestionId(starterIndex: number): string {
    if (starterIndex === 0 || this.surveyEditor$().questions.length === 0) {
      return null;
    }

    return this.surveyEditor$().questions[starterIndex - 1].id;
  }

  private findPreviousHiddenQuestionId(starterIndex: number): string {
    if (starterIndex === 0 || this.surveyEditor$().questionsHidden.length === 0) {
      return null;
    }

    return this.surveyEditor$().questionsHidden[starterIndex - 1].id;
  }

  private copyQuestion(questionToCopy: Question): void {
    const questionToAdd = QuestionFormInstanceByType(questionToCopy.type);
    const { activeIndex, nextId, pageId } = this.mapAddQuestionIds();

    questionToCopy.id = `${QuestionPrefix.Question}${nextId}`;
    questionToCopy.pageId = pageId;

    questionToAdd.init(questionToCopy);

    this.pushQuestion(activeIndex, questionToAdd);
  }

  private mapDuplicatedQuestionCode(code: string): string {
    const takenCodes = new Set([...this.surveyEditor$().questions, ...this.surveyEditor$().questionsHidden].map((question) => question.code?.toLowerCase()));
    let repeat = 1;
    let duplicatedCode = `${code}${DuplicatedCodeSuffix}`;

    while (takenCodes.has(duplicatedCode.toLowerCase())) {
      repeat++;
      duplicatedCode = `${code}${DuplicatedCodeSuffix}${repeat}`;
    }

    return duplicatedCode;
  }

  private setCarouselOptionsFromRatingScale(form: FormGroup, question: Question): void {
    const options = form.get('options') as FormArray;
    options.clear();

    for (let scaleValue = 1; scaleValue <= question.ratingScale; scaleValue++) {
      let text = `${scaleValue}`;
      if (scaleValue === 1 && question.minValueText) {
        text = question.minValueText;
      }
      if (scaleValue === question.ratingScale && question.maxValueText) {
        text = question.maxValueText;
      }

      const { id, value } = GetNextId(options.value, 'opt');
      options.push(new QuestionOptionForm({ id, value, text } as QuestionOption).form);
    }
  }
}
